import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Image, Plus, Users, User, X } from 'lucide-react';
import { ScheduledPostDto, ScheduledPostStatus } from '@api/ChallengeClient';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { CommunitySelect } from '../components/community-select';
import { ConfirmationModal } from '../components/confirmation-modal';
import { DatePicker } from '../components/date-picker';
import { Filters } from '../components/filters';
import { PageHeader } from '../components/page-header';
import { CellProps, ReactColumn, TableComponent } from '../components/table';
import { Tooltip } from '../components/tooltip';
import { Api } from '../core/api';
import { I18n } from '../core/i18n';
import { toast } from '../core/toast';
import { injectTSDI } from '../core/tsdi';
import { PostModal } from './post-modal';
import { ScheduledPostStatusFilter, useFeedPlanningStore } from './store';

const STATUS_LABELS: Record<ScheduledPostStatus, string> = {
    [ScheduledPostStatus.DRAFT]: 'Draft',
    [ScheduledPostStatus.PLANNED]: 'Planned',
    [ScheduledPostStatus.PENDING]: 'Pending',
    [ScheduledPostStatus.PUBLISHED]: 'Published',
    [ScheduledPostStatus.FAILED]: 'Failed'
};

const STATUS_VARIANTS: Record<
    ScheduledPostStatus,
    'muted' | 'info' | 'success' | 'warning'
> = {
    [ScheduledPostStatus.DRAFT]: 'muted',
    [ScheduledPostStatus.PLANNED]: 'info',
    [ScheduledPostStatus.PENDING]: 'info',
    [ScheduledPostStatus.PUBLISHED]: 'success',
    [ScheduledPostStatus.FAILED]: 'warning'
};

const CONTENT_PREVIEW_LENGTH = 80;

function contentPreview(content: string): string {
    const normalized = content.replace(/\s+/g, ' ').trim();
    return normalized.length > CONTENT_PREVIEW_LENGTH
        ? `${normalized.slice(0, CONTENT_PREVIEW_LENGTH)}…`
        : normalized;
}

function timeZoneAbbr(date: Date): string {
    return (
        new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
}

function communityLabel(count: number): string {
    return `${count} ${count === 1 ? 'community' : 'communities'}`;
}

const PostMeta = ({
    icon,
    label
}: {
    icon: React.ReactNode;
    label: string;
}) => (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        {label}
    </span>
);

type PostModalState =
    | { mode: 'closed' }
    | { mode: 'create' }
    | { mode: 'edit'; post: ScheduledPostDto }
    | { mode: 'view'; post: ScheduledPostDto }
    | { mode: 'duplicate'; source: ScheduledPostDto };

export const FeedPlanning = () => {
    const { challengeClient } = injectTSDI(Api);
    const { formatDateTime } = injectTSDI(I18n);

    const {
        posts,
        status,
        setStatus,
        communityId,
        setCommunityId,
        from,
        setFrom,
        to,
        setTo,
        clearFilters,
        setSearchTerm,
        dispatch,
        refresh
    } = useFeedPlanningStore();

    const [deleteTarget, setDeleteTarget] =
        React.useState<ScheduledPostDto | null>(null);

    const [modalState, setModalState] = React.useState<PostModalState>({
        mode: 'closed'
    });

    const handleCreateClick = () => setModalState({ mode: 'create' });

    const handleEditClick = (post: ScheduledPostDto) =>
        setModalState({ mode: 'edit', post });

    const handleViewClick = (post: ScheduledPostDto) =>
        setModalState({ mode: 'view', post });

    const handleDuplicateClick = (post: ScheduledPostDto) =>
        setModalState({ mode: 'duplicate', source: post });

    const handleCancel = async (post: ScheduledPostDto) => {
        try {
            await challengeClient.support.scheduledPostSupportControllerCancelSchedule(
                post.id
            );
            toast.success('Post schedule cancelled');
            refresh();
        } catch {
            toast.error('Failed to cancel schedule');
        }
    };

    const handleRetry = async (post: ScheduledPostDto) => {
        try {
            await challengeClient.support.scheduledPostSupportControllerRetry(
                post.id
            );
            toast.success('Post retry initiated');
            refresh();
        } catch {
            toast.error('Failed to retry post');
        }
    };

    const columns: ReactColumn<ScheduledPostDto>[] = [
        {
            Header: 'Content',
            id: 'content',
            disableSortBy: true,
            Cell: ({
                row: { original: post }
            }: CellProps<ScheduledPostDto, unknown>) => {
                const failedTarget = post.targets.find(
                    (target) => target.errorMessage
                );

                return (
                    <div
                        onClick={() =>
                            post.status === ScheduledPostStatus.PUBLISHED
                                ? handleViewClick(post)
                                : handleEditClick(post)
                        }
                        className="cursor-pointer py-1"
                    >
                        <p className="text-sm font-bold">
                            {contentPreview(post.contentMarkdown)}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-4">
                            <PostMeta
                                icon={<User className="size-4" />}
                                label={post.authorName}
                            />
                            <Tooltip
                                hideIcon
                                title={post.targets
                                    .map((target) => target.communityName)
                                    .join(', ')}
                            >
                                <span>
                                    <PostMeta
                                        icon={<Users className="size-4" />}
                                        label={communityLabel(
                                            post.targets.length
                                        )}
                                    />
                                </span>
                            </Tooltip>
                            {post.image && (
                                <PostMeta
                                    icon={<Image className="size-4" />}
                                    label="1 image"
                                />
                            )}
                        </div>
                        {post.status === ScheduledPostStatus.FAILED &&
                            failedTarget?.errorMessage && (
                                <p className="mt-1 text-xs text-warning">
                                    {failedTarget.errorMessage}
                                </p>
                            )}
                    </div>
                );
            }
        },
        {
            Header: 'Status',
            accessor: 'status',
            disableSortBy: true,
            style: { width: 130, whiteSpace: 'nowrap' },
            Cell: ({
                value
            }: CellProps<ScheduledPostDto, ScheduledPostStatus>) => (
                <Badge variant={STATUS_VARIANTS[value]}>
                    {STATUS_LABELS[value]}
                </Badge>
            )
        },
        {
            Header: 'Schedule',
            id: 'publishedDate',
            style: { width: 220, whiteSpace: 'nowrap' },
            Cell: ({
                row: { original: post }
            }: CellProps<ScheduledPostDto, unknown>) => {
                if (!post.publishedDate) {
                    return <span className="text-sm">—</span>;
                }

                const publishDate = new Date(post.publishedDate);
                const isFuturePublish = publishDate.getTime() > Date.now();

                return (
                    <div>
                        <p className="text-sm">
                            {formatDateTime(post.publishedDate)}{' '}
                            {timeZoneAbbr(publishDate)}
                        </p>
                        {isFuturePublish && (
                            <p className="text-xs text-primary">
                                {formatDistanceToNow(publishDate, {
                                    addSuffix: true
                                })}
                            </p>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <>
            <PageHeader
                title="Feed planning"
                description="Plan and broadcast posts across communities"
                actions={
                    <Button onClick={handleCreateClick}>
                        <Plus />
                        New post
                    </Button>
                }
            />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <div className="w-40">
                    <Filters<ScheduledPostStatusFilter, false>
                        label="Status"
                        value={status}
                        options={[
                            { label: 'All', value: 'ALL' },
                            {
                                label: 'Draft',
                                value: ScheduledPostStatus.DRAFT
                            },
                            {
                                label: 'Planned',
                                value: ScheduledPostStatus.PLANNED
                            },
                            {
                                label: 'Published',
                                value: ScheduledPostStatus.PUBLISHED
                            },
                            {
                                label: 'Failed',
                                value: ScheduledPostStatus.FAILED
                            }
                        ]}
                        onChange={setStatus}
                    />
                </div>

                <div className="w-64">
                    <CommunitySelect
                        label="Community"
                        value={communityId}
                        onChange={setCommunityId}
                    />
                </div>

                <div className="w-48">
                    <DatePicker
                        label="From"
                        value={from}
                        clearable
                        onChange={(value) => setFrom(value)}
                    />
                </div>
                <div className="w-48">
                    <DatePicker
                        label="To"
                        value={to}
                        clearable
                        onChange={(value) => setTo(value)}
                    />
                </div>

                <Button variant="ghost" onClick={clearFilters}>
                    Clear
                    <X />
                </Button>
            </div>

            <TableComponent<ScheduledPostDto[]>
                columns={columns}
                items={posts}
                emptyText="No scheduled posts"
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search posts..."
                contextMenu={[
                    {
                        label: 'Retry',
                        hidden: (post) =>
                            post.status !== ScheduledPostStatus.FAILED,
                        action: (post) => void handleRetry(post)
                    },
                    {
                        label: 'Schedule',
                        hidden: (post) =>
                            post.status !== ScheduledPostStatus.DRAFT,
                        action: (post) => handleEditClick(post)
                    },
                    {
                        label: 'Edit',
                        hidden: (post) =>
                            post.status === ScheduledPostStatus.PUBLISHED,
                        action: (post) => handleEditClick(post)
                    },
                    {
                        label: 'Duplicate',
                        hidden: () => false,
                        action: (post) => handleDuplicateClick(post)
                    },
                    {
                        label: 'Cancel schedule',
                        hidden: (post) =>
                            post.status !== ScheduledPostStatus.PLANNED,
                        action: (post) => void handleCancel(post)
                    },
                    {
                        label: 'View',
                        hidden: (post) =>
                            post.status !== ScheduledPostStatus.PUBLISHED,
                        action: (post) => handleViewClick(post)
                    },
                    {
                        label: 'Delete',
                        hidden: (post) =>
                            post.status === ScheduledPostStatus.PUBLISHED,
                        action: (post) => setDeleteTarget(post)
                    }
                ]}
            />

            <ConfirmationModal
                open={Boolean(deleteTarget)}
                title="Delete post"
                buttonText="Delete"
                destructive
                onModalClose={() => setDeleteTarget(null)}
                submitHandler={async () => {
                    if (!deleteTarget) {
                        return false;
                    }
                    await challengeClient.support.scheduledPostSupportControllerDelete(
                        deleteTarget.id
                    );
                    toast.success('Post deleted');
                    refresh();
                    return true;
                }}
            >
                <p className="text-sm">
                    Are you sure you want to delete this post? This action
                    cannot be undone.
                </p>
            </ConfirmationModal>

            <PostModal
                open={modalState.mode !== 'closed'}
                post={
                    modalState.mode === 'edit' || modalState.mode === 'view'
                        ? modalState.post
                        : undefined
                }
                duplicateOf={
                    modalState.mode === 'duplicate'
                        ? modalState.source
                        : undefined
                }
                readOnly={modalState.mode === 'view'}
                onClose={() => setModalState({ mode: 'closed' })}
                onSaved={refresh}
            />
        </>
    );
};
